import * as Sentry from "@sentry/browser";
import { useMemo } from "react";
import useSWRSubscription from "swr/subscription";

import { dbFirestore } from "../../firestore";

import type { BlockedUser, WithId } from "../../firestore/types";

/**
 * 呼び出し側が loading と error を区別できるようにするために分けている。まとめて
 * 「リスト不明」にすると、一瞬の読み込み中と恒久的な失敗に同じ扱いしかできない。
 */
type BlockedUsersState =
  | { status: "loading" }
  | { status: "loaded"; blockedUsers: WithId<BlockedUser>[] }
  | { status: "error" };

type BlockedUsersKey = readonly ["blockedUsers", string];

/**
 * useSWRSubscription を使うのは、複数のコンポーネントが同時にこのフックを呼んでも
 * キーが同じなら onSnapshot が 1 本に集約されるため。素の useState + useEffect だと
 * 呼んだ数だけ購読が張られ、「唯一の購読」が実装で担保されない。
 * ユーザー切り替え時にキーごと変わるので、前ユーザーの結果を引きずることもない。
 */
export const useBlockedUsers = ({
  userId,
}: {
  userId: string | undefined;
}): BlockedUsersState => {
  const { data, error } = useSWRSubscription<
    WithId<BlockedUser>[],
    Error,
    BlockedUsersKey | null
  >(
    userId ? (["blockedUsers", userId] as const) : null,
    ([, uid], { next }) => {
      // orderBy はクエリに入れない。createdAt を持たない doc がクエリ結果から丸ごと
      // 除外され、そのユーザーのブロックが黙って効かなくなるため。並べ替えは表示側で行う。
      return dbFirestore.blockedUsers(uid).onSnapshot(
        (snapshot) => {
          next(
            null,
            snapshot.docs.flatMap((doc) => {
              const blockedUser = doc.data();
              if (blockedUser === null) return [];
              return [{ ...blockedUser, id: doc.id }];
            })
          );
        },
        (subscribeError) => {
          // 失敗すると復帰しない。画面上は何も出ないため、気づけるよう Sentry へ送る。
          Sentry.captureException(subscribeError);
          next(subscribeError);
        }
      );
    }
  );

  return useMemo(() => {
    if (!userId) return { status: "loaded", blockedUsers: [] };
    if (error) return { status: "error" };
    if (data === undefined) return { status: "loading" };
    return { status: "loaded", blockedUsers: data };
  }, [userId, error, data]);
};
